import { useRef } from 'react';
import { NextSeo } from 'next-seo';
import getMetadata from '@/utils/getMetadata';
import localeManager from '@/utils/localeManager';
import useLangMenuBar from '@/hooks/useLangMenuBar';
import Footer from '@/components/Footer';
import PuntosParaEnvios from '@/components/PuntosParaEnvios';
import {
  filterPoints,
  computeCenterFromPoints,
  parseHopQuery,
  applyMapCenter
} from '@/utils/hopPuntos';
import { fetchHopPuntos } from '@/utils/hopPuntosServer';

const ConoceLosPuntosHop = ({
  locale,
  points,
  footer,
  menu,
  dict,
  countryMapCenter,
  countryMapZoom,
  hasPointsData,
  fitPointsToBounds,
  initialServiceFilter,
  debugSellerBadge,
  hideNavBar,
  noindex
}) => {
  const root = useRef();
  useLangMenuBar(menu);

  return (
    <main ref={root} className="relative site-max mx-auto puntos-para-envios">
      <NextSeo
        {...getMetadata({
          locale,
          title: dict?.shipping_points?.seo?.title,
          description: dict?.shipping_points?.seo?.description,
          pathname: '/conoce-los-puntos-hop'
        })}
        noindex={noindex}
        nofollow={noindex}
      />
      <PuntosParaEnvios
        points={points}
        dict={dict}
        countryMapCenter={countryMapCenter}
        countryMapZoom={countryMapZoom}
        hasPointsData={hasPointsData}
        fitPointsToBounds={fitPointsToBounds}
        initialServiceFilter={initialServiceFilter}
        debugSellerBadge={debugSellerBadge}
        hideNavBar={hideNavBar}
      />
      <Footer footer={footer} locale={locale} />
    </main>
  );
};

export async function getServerSideProps({ req, res, query, locale: nextLocale, defaultLocale }) {
  const { locale, dict } = await localeManager.getLocaleResources(req, {}, nextLocale || defaultLocale);

  const data = await fetchHopPuntos(dict.country_short_name);
  const params = parseHopQuery(query);
  const debugSellerBadge =
    process.env.NODE_ENV === 'development' && query.debugSellerBadge === '1';
  const initialServiceFilter = params.searchMode;
  const clientOnlyFocusModes = ['pickup', 'dropoff', 'buy'];
  const queryMode = clientOnlyFocusModes.includes(params.mode) ? 'all' : params.mode;
  const shouldFilter = params.active || !['all', ...clientOnlyFocusModes].includes(params.mode);
  const points = shouldFilter
    ? filterPoints({ points: data, ...params, mode: queryMode })
    : data;
  const pointsForInitialView = filterPoints({ points, mode: initialServiceFilter });

  // Indexable sin segmentación. Con params de segmentación la URL sale del
  // índice; el canonical queda limpio.
  if (params.active) res.setHeader('X-Robots-Tag', 'noindex, nofollow');

  const center = computeCenterFromPoints(pointsForInitialView);
  const centeredDict = center
    ? { ...dict, map: { ...dict.map, lat: center.lat, lng: center.lng } }
    : dict;
  const adjustedDict = params.active
    ? applyMapCenter(centeredDict, pointsForInitialView, params)
    : centeredDict;
  const fitPointsToBounds =
    params.center !== 'auto' &&
    !(params.centerLat !== null && params.centerLng !== null) &&
    params.zoom === null;

  const hasPointsData = data.pickup.length > 0 || data.dropOff.length > 0;

  return {
    props: {
      locale,
      footer: dict.home.footer,
      menu: dict.menu,
      dict: adjustedDict,
      countryMapCenter: { lat: dict.map.lat, lng: dict.map.lng },
      countryMapZoom: dict.map.overviewZoom || 4,
      points,
      hasPointsData,
      fitPointsToBounds,
      initialServiceFilter,
      debugSellerBadge,
      hideNavBar: query.hideNavBar === 'true',
      noindex: params.active
    }
  };
}

export default ConoceLosPuntosHop;